import type { SubmitEvent } from 'react';
import { useCallback, useState } from 'react';
import { AlertCircle, LayoutGrid, Lock, MessageSquare, Shield, User } from 'lucide-react';

import { SetNewPasswordForm } from './SetNewPasswordForm';

import { useLogin } from '@/api/endpoints/auth';
import { useSSOOptions } from '@/hooks/use-sso-options';
import { PlainLayout } from '@/modus/PlainLayout';
import { Wordmark } from '@/modus/Wordmark';
import { Button } from '@/ui/Button';
import { Dialog } from '@/ui/dialog/Dialog';
import { InputNext } from '@/ui/InputNext';
import { extractHTTPError } from '@/utils/http-client';
import { getProviderConfig } from '@/utils/sso-providers';

const FEATURES = [
  { Icon: Shield, label: 'Role-based access and audit controls' },
  { Icon: MessageSquare, label: 'Grounded agents and enterprise context' },
  { Icon: LayoutGrid, label: 'Workflows, workbench, and data products' },
] as const;

export function LoginPage({ redirectTo, initialError }: { redirectTo?: string; initialError?: string }) {
  const [error, setError] = useState<string | null>(initialError ?? null);

  const [login, { isPending: isLoggingIn, isSuccess: isLoginSuccess, data: loginData, reset: resetLogin }] = useLogin();
  const { ssoOptions, ssoConfigError, handleSSOLogin, isInitiatingSSO, isLoadingSSOConfig, isStandardLoginAllowed } =
    useSSOOptions({ onError: setError });

  const passwordUpdate = loginData?.password_update_required ? loginData : null;

  const handleLogin = useCallback(
    async (e: SubmitEvent<HTMLFormElement>) => {
      e.preventDefault();
      setError(null);

      const formData = new FormData(e.currentTarget);
      const { error: loginError } = await login({
        username: (formData.get('username') ?? '').toString(),
        password: (formData.get('password') ?? '').toString(),
        redirect_to: redirectTo,
      });

      if (loginError) {
        setError(extractHTTPError(loginError, 'An unexpected error occurred'));
      }
    },
    [login, redirectTo],
  );

  const handleCancelPasswordUpdate = useCallback(() => {
    resetLogin();
    setError(null);
  }, [resetLogin]);

  const isLoading = isLoggingIn || (isLoginSuccess && !passwordUpdate);

  return (
    <PlainLayout main className="flex h-screen overflow-hidden">
      {/* Left panel — branding, hidden on small screens */}
      <div className="hidden lg:flex flex-1 flex-col border-r border-white/10 bg-tlai-1 px-14 py-10">
        <Wordmark size={120} loading={isLoadingSSOConfig || isLoading} />

        <div className="flex flex-1 flex-col justify-center max-w-sm">
          <p className="mb-4 text-xs font-semibold uppercase tracking-widest text-white/40">
            Secure Enterprise Workspace
          </p>
          <h1 className="mb-6 text-5xl font-bold leading-tight text-white">Sign in to your Enterprise AI Workspace.</h1>
          <h2 className="mb-6 text-lg leading-tight text-white">Powered by Tessera AI</h2>
          <p className="mb-8 text-sm leading-relaxed text-white/50">
            Access governed agents, ERP data tools, remediation workflows, and knowledge-backed conversations.
          </p>
          <div className="mb-8 h-px w-full bg-white/10" />
          <ul className="flex flex-col gap-3.5">
            {FEATURES.map(({ Icon, label }) => (
              <li key={label} className="flex items-center gap-3 text-sm font-semibold text-white">
                <Icon className="size-4 shrink-0 text-white/50" />
                {label}
              </li>
            ))}
          </ul>
        </div>
      </div>

      {/* Right panel — login form */}
      <div className="flex flex-1 flex-col items-center justify-center bg-tlai-0 px-6 lg:px-14">
        <div className="w-full max-w-sm">
          <Wordmark size={220} loading={isLoadingSSOConfig || isLoading} className="mb-10 lg:hidden mx-auto" />
          <h2 className="mb-8 hidden text-3xl font-bold text-white lg:block">Welcome back</h2>

          <div className="flex flex-col gap-4 rounded-2xl bg-tlai-3 p-6">
            {passwordUpdate ? (
              <SetNewPasswordForm
                username={passwordUpdate.username}
                changePasswordToken={passwordUpdate.change_password_token}
                redirectTo={redirectTo}
                onCancel={handleCancelPasswordUpdate}
              />
            ) : (
              <>
                {!isLoadingSSOConfig && ssoConfigError && (
                  <div className="flex w-full gap-2 rounded-md bg-red-500/10 px-3 py-2 text-red-500">
                    <AlertCircle className="size-4 shrink-0" />
                    <p className="text-xs">Unable to communicate with the server.</p>
                  </div>
                )}
                {error && (
                  <div className="flex w-full items-center gap-2 rounded-md bg-red-500/10 px-3 py-2 text-red-500">
                    <AlertCircle className="size-4 shrink-0" />
                    <p className="text-sm">{error}</p>
                  </div>
                )}
                {ssoOptions?.map((provider) => {
                  const config = getProviderConfig(provider.id);
                  const Icon = config.Icon;
                  const displayName =
                    config.displayName !== provider.id ? config.displayName : provider.name || provider.id;

                  return (
                    <Button
                      key={provider.id}
                      variant="secondary"
                      className="text-sm text-white/70"
                      Icon={Icon}
                      disabled={isLoadingSSOConfig || isLoading || isInitiatingSSO}
                      onClick={() => handleSSOLogin(provider.id)}
                    >
                      Sign in with {displayName}
                    </Button>
                  );
                })}
                {isStandardLoginAllowed && !!ssoOptions && (
                  <div className="flex items-center gap-2 text-sm text-white/40">
                    <div className="h-px flex-1 bg-white/10" />
                    <span className="shrink-0">Or continue with</span>
                    <div className="h-px flex-1 bg-white/10" />
                  </div>
                )}
                {!isLoadingSSOConfig && isStandardLoginAllowed && (
                  <form onSubmit={handleLogin} method="post" className="flex w-full flex-col gap-3 login">
                    <InputNext
                      id="username"
                      name="username"
                      type="text"
                      autoComplete="username"
                      className="bg-white/3"
                      startAdornment={<User className="size-4 text-muted-foreground" />}
                      placeholder="email address"
                      disabled={isLoadingSSOConfig || isLoading || isInitiatingSSO}
                      required
                    />
                    <InputNext
                      id="password"
                      name="password"
                      type="password"
                      autoComplete="current-password"
                      className="bg-white/3"
                      startAdornment={<Lock className="size-4 text-muted-foreground" />}
                      placeholder="password"
                      disabled={isLoadingSSOConfig || isLoading || isInitiatingSSO}
                      required
                    />
                    <Button
                      type="submit"
                      disabled={isLoadingSSOConfig || isLoading || isInitiatingSSO}
                      className="w-full"
                    >
                      Sign In
                    </Button>
                    <Dialog>
                      <Dialog.Trigger asChild>
                        <Button type="button" variant="link" className="text-sm text-white/70">
                          Forgot password?
                        </Button>
                      </Dialog.Trigger>
                      <Dialog.Content>
                        <Dialog.Title>Forgot Password</Dialog.Title>
                        Please contact your administrator to reset your password.
                      </Dialog.Content>
                    </Dialog>
                  </form>
                )}
              </>
            )}
          </div>
        </div>
      </div>
    </PlainLayout>
  );
}
